import type { FolderArtifactData, SkillOfferData } from '@newfront-insurance/ai-api';
import type { UIDataTypes, UIMessagePart, UITools } from 'ai';

import { getShellSourceMetadata, type SourceDocumentPart } from './sources';

export type SkillArtifactData = Omit<SkillOfferData, 'kind'> & {
  kind: 'skill';
};

export type { FolderArtifactData };

export type ShellArtifactCardData = SkillArtifactData | FolderArtifactData;
export type ShellArtifactRendererKey = 'skillOffer' | 'folderArtifact';
export type ShellArtifactPart = UIMessagePart<UIDataTypes, UITools>;

export type ShellArtifactRenderItem = {
  fileUuid: string;
  rendererKey: ShellArtifactRendererKey;
  part: ShellArtifactPart;
};

type ArtifactCandidate = {
  source?: { status: 'blocked' } | { status: 'ready'; data: ShellArtifactCardData };
  legacy?: ShellArtifactCardData;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function getLegacyArtifactData(part: ShellArtifactPart): ShellArtifactCardData | null {
  if (part.type !== 'data-skillOffer' && part.type !== 'data-folderArtifact') {
    return null;
  }

  const data = 'data' in part ? part.data : undefined;
  if (!isRecord(data) || typeof data.fileUuid !== 'string' || typeof data.filename !== 'string') {
    return null;
  }

  if (part.type === 'data-skillOffer' && data.kind === 'skill') {
    return {
      fileUuid: data.fileUuid,
      filename: data.filename,
      kind: 'skill',
      mediaType: getOptionalString(data.mediaType),
      command: getOptionalString(data.command),
      commandHandle: getOptionalString(data.commandHandle),
    };
  }

  if (part.type === 'data-folderArtifact' && data.kind === 'folder') {
    return {
      fileUuid: data.fileUuid,
      filename: data.filename,
      kind: 'folder',
      mediaType: getOptionalString(data.mediaType),
    };
  }

  return null;
}

function getSourceArtifactCandidate(
  part: ShellArtifactPart,
): { fileUuid: string; state: NonNullable<ArtifactCandidate['source']> } | null {
  if (part.type !== 'source-document') {
    return null;
  }

  const source = part as unknown as SourceDocumentPart;
  const metadata = getShellSourceMetadata(source);
  if (!metadata?.fileUuid || (metadata.kind !== 'skill' && metadata.kind !== 'folder')) {
    return null;
  }

  if (metadata.pending || source.pending || metadata.failed) {
    return { fileUuid: metadata.fileUuid, state: { status: 'blocked' } };
  }

  const filename = source.filename ?? source.title;
  if (metadata.kind === 'skill') {
    return {
      fileUuid: metadata.fileUuid,
      state: {
        status: 'ready',
        data: {
          fileUuid: metadata.fileUuid,
          filename,
          kind: 'skill',
          mediaType: source.mediaType,
          command: metadata.skillCommand ?? undefined,
          commandHandle: metadata.skillCommandHandle ?? undefined,
        },
      },
    };
  }

  return {
    fileUuid: metadata.fileUuid,
    state: {
      status: 'ready',
      data: {
        fileUuid: metadata.fileUuid,
        filename,
        kind: 'folder',
        mediaType: source.mediaType,
      },
    },
  };
}

function mergeArtifactCandidate({ source, legacy }: ArtifactCandidate): ShellArtifactCardData | null {
  if (source?.status === 'blocked') {
    return null;
  }

  const preferred = source?.data ?? legacy;
  if (!preferred) {
    return null;
  }

  if (preferred.kind === 'skill') {
    const legacySkill = legacy?.kind === 'skill' ? legacy : undefined;
    return {
      ...preferred,
      command: preferred.command ?? legacySkill?.command,
      commandHandle: preferred.commandHandle ?? legacySkill?.commandHandle,
    };
  }

  return preferred;
}

function toRenderItem(data: ShellArtifactCardData): ShellArtifactRenderItem {
  const rendererKey: ShellArtifactRendererKey = data.kind === 'skill' ? 'skillOffer' : 'folderArtifact';

  return {
    fileUuid: data.fileUuid,
    rendererKey,
    part: {
      type: `data-${rendererKey}`,
      data,
    } as ShellArtifactPart,
  };
}

/**
 * Normalizes canonical source-document metadata and legacy artifact data parts into one card per file UUID.
 * Source metadata is authoritative; legacy command fields are retained as a compatibility fallback.
 */
export function buildShellArtifactRenderModel(parts: readonly ShellArtifactPart[]): ShellArtifactRenderItem[] {
  const candidates = new Map<string, ArtifactCandidate>();

  parts.forEach((part) => {
    const source = getSourceArtifactCandidate(part);
    const legacy = getLegacyArtifactData(part);
    const fileUuid = source?.fileUuid ?? legacy?.fileUuid;
    if (!fileUuid) {
      return;
    }

    const candidate = candidates.get(fileUuid) ?? {};
    candidates.set(fileUuid, {
      source: source?.state ?? candidate.source,
      legacy: legacy ?? candidate.legacy,
    });
  });

  return Array.from(candidates.values()).flatMap((candidate) => {
    const artifact = mergeArtifactCandidate(candidate);
    return artifact ? [toRenderItem(artifact)] : [];
  });
}

export function isLegacyShellArtifactPart(part: ShellArtifactPart): boolean {
  return part.type === 'data-skillOffer' || part.type === 'data-folderArtifact';
}
